// src/pages/ConfiguracionPage.tsx
import { useState, useEffect } from 'react'
import api from '../services/api'
import { Settings, Clock, Lock, Shield, Save, Loader2, AlertCircle, CheckCircle } from 'lucide-react'

interface ConfigItem {
  clave: string
  valor: string
  descripcion: string
}

export function ConfiguracionPage() {
  const [config, setConfig] = useState<ConfigItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [editValues, setEditValues] = useState<Record<string, string>>({})

  useEffect(() => {
    fetchConfig()
  }, [])

  const fetchConfig = async () => {
    try {
      const { data } = await api.get('/configuracion')
      setConfig(data.data || [])
      const values: Record<string, string> = {}
      data.data?.forEach((item: ConfigItem) => {
        values[item.clave] = item.valor
      })
      setEditValues(values)
    } catch (err) {
      console.error('Error fetching config:', err)
      setError('Error al cargar la configuracion')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (clave: string) => {
    setSaving(clave)
    setError('')
    setSuccess('')

    try {
      await api.put(`/configuracion/${clave}`, { valor: editValues[clave] })
      setSuccess(`Configuracion "${clave}" actualizada correctamente`)
      setTimeout(() => setSuccess(''), 3000)
      fetchConfig()
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { error?: string } } }
        setError(axiosError.response?.data?.error || 'Error al guardar')
      } else {
        setError('Error al guardar configuracion')
      }
    } finally {
      setSaving(null)
    }
  }

  const initializeConfig = async () => {
    setLoading(true)
    try {
      await api.post('/configuracion/inicializar')
      setSuccess('Configuracion inicializada correctamente')
      fetchConfig()
    } catch (err) {
      setError('Error al inicializar configuracion')
    } finally {
      setLoading(false)
    }
  }

  const getIcon = (clave: string) => {
    switch (clave) {
      case 'session_duration_minutes':
        return <Clock className="w-6 h-6 text-blue-500" />
      case 'max_login_attempts':
        return <Lock className="w-6 h-6 text-orange-500" />
      case 'lockout_duration_minutes':
        return <Shield className="w-6 h-6 text-red-500" />
      default:
        return <Settings className="w-6 h-6 text-gray-500" />
    }
  }

  const getLabel = (clave: string) => {
    switch (clave) {
      case 'session_duration_minutes':
        return 'Duracion de Sesion (minutos)'
      case 'max_login_attempts':
        return 'Intentos Maximos de Login'
      case 'lockout_duration_minutes':
        return 'Duracion de Bloqueo (minutos)'
      default:
        return clave
    }
  }

  const getHelperText = (clave: string) => {
    switch (clave) {
      case 'session_duration_minutes':
        return 'Tiempo que la sesion permanece activa antes de expirar (5-480 min)'
      case 'max_login_attempts':
        return 'Numero de intentos fallidos antes de bloquear la cuenta (1-10)'
      case 'lockout_duration_minutes':
        return 'Tiempo que la cuenta permanece bloqueada (5-1440 min)'
      default:
        return ''
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <Settings className="w-8 h-8 mr-3 text-primary-600" />
          Configuracion del Sistema
        </h1>
        <p className="text-gray-500 mt-2">Gestiona los parametros de seguridad y sesion del sistema</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {config.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">Sin configuracion</h3>
          <p className="text-gray-500 mb-6">La tabla de configuracion no existe o esta vacia.</p>
          <button
            onClick={initializeConfig}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Inicializar Configuracion
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {config.map((item) => (
            <div key={item.clave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <div className="p-3 bg-gray-50 rounded-lg mr-4">
                    {getIcon(item.clave)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{getLabel(item.clave)}</h3>
                    <p className="text-sm text-gray-500 mt-1">{item.descripcion || getHelperText(item.clave)}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex items-end gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor actual</label>
                  <input
                    type="number"
                    value={editValues[item.clave] || item.valor}
                    onChange={(e) => setEditValues({ ...editValues, [item.clave]: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-400 mt-1">{getHelperText(item.clave)}</p>
                </div>
                <button
                  onClick={() => handleSave(item.clave)}
                  disabled={saving === item.clave || editValues[item.clave] === item.valor}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {saving === item.clave ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}

          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">Informacion Importante</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>La duracion de sesion afecta a los nuevos logins, no a las sesiones activas.</li>
              <li>Los usuarios bloqueados pueden desbloquear su cuenta mediante recuperacion de contrasena.</li>
              <li>Los cambios en la configuracion se aplican inmediatamente.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
